import React, { useState, useCallback, useMemo, useContext, Fragment, forwardRef } from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip/TooltipPrimitive';
import TooltipPrimitive from '@atlaskit/tooltip/TooltipPrimitive';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { EDITOR2_SHORTCUTS } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { ConfluenceRecapPublishSpotlight } from '@confluence/confluence-recap/entry-points/ConfluenceRecapPublishSpotlight.tsx';
import { useRecapSpotlightEligibility } from '@confluence/confluence-recap/entry-points/useRecapSpotlightEligibility.ts';

// oxlint-disable-next-line import/no-cycle
import { DefaultPublishModalButton } from './DefaultPublishModalButton.tsx';
import { UpdateSplitButton } from './UpdateSplitButton.tsx';
// eslint-disable-next-line import/no-cycle
import { ScheduledButton } from './ScheduledButton.tsx';
import { SHORTCUT_KEYS } from './constants.ts';
import { PublishModalContext, withPublishModalContextProvider } from './PublishModalContext.tsx';
import type { PublishModalContextProviderExternalProps } from './PublishModalContext.tsx';
import { PublishModalDialogs } from './PublishModalDialogs.tsx';
import { usePublishModalActions } from './usePublishModalActions.tsx';
import { usePrePublishTitleValidation } from './usePrePublishTitleValidation.ts';
import type { AutoFocusTargetOptions } from './types.ts';

// same styles as a tooltip but a larger max width to accomodate the long content and shortcut
const wideTooltipStyle = css({
	background: token('color.background.neutral.bold'),
	color: token('color.text.inverse'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	font: token('font.body.small'),
	maxWidth: '350px',
	padding: `${token('space.025')} ${token('space.075')}`,
});

export const WideTooltip = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function WideTooltip(
	{ children, ...props },
	ref,
) {
	return (
		<TooltipPrimitive {...props} ref={ref} css={wideTooltipStyle}>
			{children}
		</TooltipPrimitive>
	);
}) as React.ForwardRefExoticComponent<TooltipPrimitiveProps>;

const tooltipTextStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.025'),
});

export const TooltipText = ({ children }: { children: any }): React.JSX.Element => (
	<div css={tooltipTextStyle}>{children}</div>
);

export type PublishButtonWithMoreOptionsProps = PublishModalContextProviderExternalProps &
	PublishButtonWithMoreOptionsUnwrappedProps;

type PublishButtonWithMoreOptionsUnwrappedProps = {
	publishButtonDisabled: boolean | undefined;
	userViewingPreview?: boolean;
	isUnpublishedDraft: boolean;
	flags: FlagsStateContainer;
	saveDraft: (() => void) | undefined;
	contentType: string;
	title: string | null;
	savingInProgress: boolean;
	shouldShowWorldIcon: boolean;
	shouldShowFooterButtons?: boolean;
	onHoverPublish?: () => void;
	checkIfConditionPreventingPublish?: () => Promise<boolean>;
};

const PublishButtonWithMoreOptionsUnwrapped = ({
	publishButtonDisabled,
	title,
	userViewingPreview,
	isUnpublishedDraft,
	flags,
	saveDraft,
	contentType,
	savingInProgress,
	shouldShowWorldIcon,
	shouldShowFooterButtons = true,
	onHoverPublish,
	checkIfConditionPreventingPublish,
}: PublishButtonWithMoreOptionsUnwrappedProps) => {
	const [contentId] = usePageContentId();
	const {
		currentlyScheduledPublishDate,
		setExpanded,
		retrieveEditorTitleAndContent,
		graphqlError,
		restrictionsGraphQLError,
	} = useContext(PublishModalContext);
	const { handlePreview } = usePublishModalActions();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const isRecapSpotlightEligible = useRecapSpotlightEligibility(isUnpublishedDraft);
	/**
	 * Target a field to focus on modal open.
	 */
	const [initialFocusTarget, setInitialFocusTarget] = useState<AutoFocusTargetOptions>(undefined);
	/*
	 * We have to deactivate the keyboard shortcuts for publish/update button when the publish modal is open
	 * so that there aren't duplicate shortcuts registered.
	 */
	const [shortcutIsEnabled, setShortcutIsEnabled] = useState<boolean>(true);
	const pressTracing = usePressTracing('publish-modal-button');

	const errorToReport = useMemo(() => {
		const handleError = (e) => {
			if (e) {
				if (containsNetworkError(e) || isUnauthorizedError(e)) {
					markErrorAsHandled(e);
					return undefined;
				}
				return e;
			}
		};

		if (graphqlError) {
			return handleError(graphqlError);
		}
		if (restrictionsGraphQLError) {
			return handleError(restrictionsGraphQLError);
		}
	}, [restrictionsGraphQLError, graphqlError]);

	const validatePageAndOpenModal = usePrePublishTitleValidation({
		contentId,
		contentType,
		isUnpublishedDraft,
		flags,
		retrieveEditorTitleAndContent,
		onBeforeValidation: useCallback(() => {
			saveDraft?.();
		}, [saveDraft]),
		onValidationSuccess: useCallback(() => {
			pressTracing();
			setShortcutIsEnabled(false);
			setIsOpen(true);
		}, [pressTracing]),
		analyticsSource: 'PublishButtonWithMoreOptions',
		checkIfConditionPreventingPublish,
	});

	const onPublishModalComplete = useCallback(() => {
		/** We can only re-enabled the keyboard shortcut when the modal is finished closing,
		 *  because otherwise a duplicate shortcut will exist during the modal close animation.
		 */
		setIsOpen(false);
		setShortcutIsEnabled(true);
	}, []);

	const [spaceKey] = usePageSpaceKey();

	const getPrimaryButton = () => {
		if (!isUnpublishedDraft && !currentlyScheduledPublishDate) {
			const openModal = isCompanyHubSpaceKey(spaceKey)
				? undefined
				: validatePageAndOpenModal('PublishModalChevronButton');
			return (
				<>
					<UpdateSplitButton
						openModal={openModal}
						publishButtonDisabled={publishButtonDisabled}
						shortcutIsEnabled={shortcutIsEnabled}
						saveDraft={saveDraft}
						flags={flags}
						savingInProgress={savingInProgress}
						shouldShowWorldIcon={shouldShowWorldIcon}
						contentType={contentType}
						isUnpublishedDraft={isUnpublishedDraft}
						onHoverPublish={onHoverPublish}
					/>
					{shortcutIsEnabled && !savingInProgress && (
						<>
							{!publishButtonDisabled && openModal && (
								<ShortcutListener
									key={SHORTCUT_KEYS.PBWMO1}
									accelerator={EDITOR2_SHORTCUTS.PUBLISH_WITH_COMMENT}
									listener={() => {
										setInitialFocusTarget('version comment');
										void openModal();
									}}
								/>
							)}
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO2}
								accelerator={EDITOR2_SHORTCUTS.PREVIEW}
								listener={() =>
									handlePreview({
										analyticsSource: 'KeyboardShortcut',
										isUnpublishedDraft,
									})
								}
							/>
						</>
					)}
				</>
			);
		} else {
			const publishModalButton = isRecapSpotlightEligible ? (
				<ConfluenceRecapPublishSpotlight>
					<DefaultPublishModalButton
						saveDraft={saveDraft}
						openModalWithAnalytics={validatePageAndOpenModal}
						savingInProgress={savingInProgress}
						publishButtonDisabled={publishButtonDisabled}
					/>
				</ConfluenceRecapPublishSpotlight>
			) : (
				<DefaultPublishModalButton
					saveDraft={saveDraft}
					openModalWithAnalytics={validatePageAndOpenModal}
					savingInProgress={savingInProgress}
					publishButtonDisabled={publishButtonDisabled}
				/>
			);
			return (
				<Fragment>
					{!!currentlyScheduledPublishDate ? (
						<ScheduledButton
							saveDraft={saveDraft}
							openModalWithAnalytics={validatePageAndOpenModal}
							savingInProgress={savingInProgress}
							isUnpublishedDraft={isUnpublishedDraft}
							shouldShowWorldIcon={shouldShowWorldIcon}
							publishButtonDisabled={publishButtonDisabled}
						/>
					) : (
						publishModalButton
					)}
					{shortcutIsEnabled && !savingInProgress && (
						<>
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO3}
								accelerator={EDITOR2_SHORTCUTS.PUBLISH}
								listener={validatePageAndOpenModal(
									!!currentlyScheduledPublishDate
										? 'ScheduledButton'
										: 'PublishModalPublishMoreActionsButton',
								)}
							/>
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO4}
								accelerator={EDITOR2_SHORTCUTS.PUBLISH_WITH_COMMENT}
								listener={() => {
									setInitialFocusTarget('version comment');
									setExpanded(true);
									void validatePageAndOpenModal(
										!!currentlyScheduledPublishDate
											? 'ScheduledButton'
											: 'PublishModalPublishMoreActionsButton',
									)();
								}}
							/>
							<ShortcutListener
								key={SHORTCUT_KEYS.PBWMO5}
								accelerator={EDITOR2_SHORTCUTS.PREVIEW}
								listener={() =>
									handlePreview({
										analyticsSource: 'KeyboardShortcut',
										isUnpublishedDraft,
									})
								}
							/>
						</>
					)}
				</Fragment>
			);
		}
	};

	return (
		<Fragment>
			{getPrimaryButton()}
			{isOpen && (
				<PublishModalDialogs
					isUnpublishedDraft={isUnpublishedDraft}
					contentType={contentType}
					title={title}
					publishButtonDisabled={publishButtonDisabled}
					userViewingPreview={userViewingPreview}
					initialFocusTarget={initialFocusTarget}
					shouldShowWorldIcon={shouldShowWorldIcon}
					shouldShowFooterButtons={shouldShowFooterButtons}
					onPublishModalComplete={onPublishModalComplete}
					onHoverPublish={onHoverPublish}
				/>
			)}
			{errorToReport && <ErrorDisplay error={errorToReport} />}
		</Fragment>
	);
};

export const PublishButtonWithMoreOptions: React.FC<
	PublishButtonWithMoreOptionsUnwrappedProps & PublishModalContextProviderExternalProps
> = withErrorBoundary({
	attribution: Attribution.PAGE_MODES,
})(withPublishModalContextProvider(PublishButtonWithMoreOptionsUnwrapped));
